import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Lock, ArrowRight, Trophy, Briefcase, Star, ArrowLeft, BookOpen, ClipboardCheck } from "lucide-react";

import { Navbar } from "@/components/layout/Nav";
import { CanalesLogo } from "@/components/CanalesLogo";
import { Mascot, Carlos } from "@/components/characters/Characters";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api/client";
import { findComic } from "@/data/comics";

const ExamRow = ({ exam, lang, bonus, locked }) => {
  const passed = exam.passed;
  const pct = exam.best_percent ?? 0;
  return (
    <li
      className={`flex items-center justify-between brutal-sm px-3 py-2 ${locked ? "bg-neutral-100 opacity-70" : "bg-canales-cream"}`}
      data-testid={`role-exam-${exam.slug}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {bonus && <Star className="w-3 h-3 fill-canales-yellow text-black shrink-0" />}
          {locked && <Lock className="w-3 h-3 text-neutral-500 shrink-0" />}
          <div className="font-display font-bold text-sm truncate">
            {lang === "es" ? exam.title_es : exam.title_en}
          </div>
        </div>
        <div className="text-xs font-body text-neutral-600">
          {exam.attempts ? `${exam.attempts} ${lang === "es" ? "intentos" : "attempts"}` : lang === "es" ? "Sin intentar" : "Not attempted"}
          {" · "}{exam.question_count} {lang === "es" ? "preguntas" : "questions"}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className={`font-display font-black text-sm ${pct >= 90 ? "text-canales-red" : passed ? "text-green-700" : "text-neutral-500"}`}>
          {pct ? `${pct}%` : "—"}
        </div>
        {locked ? (
          <span
            className="brutal-btn bg-neutral-300 text-neutral-700 px-2 py-1 text-xs flex items-center gap-1 cursor-not-allowed"
            data-testid={`role-exam-locked-${exam.slug}`}
          >
            <Lock className="w-3 h-3" /> {lang === "es" ? "Lee el cómic" : "Read the comic"}
          </span>
        ) : (
          <Link
            to={`/exam/${exam.slug}`}
            className="brutal-btn bg-canales-blue text-white px-2 py-1 text-xs flex items-center gap-1"
          >
            {lang === "es" ? "Abrir" : "Open"} <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    </li>
  );
};

export default function OnboardingRole() {
  const { slug } = useParams();
  const { lang } = useLang();
  const { user } = useAuth() || {};

  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Priority: authenticated user name → localStorage → empty (will prompt)
  const initialName = user?.name || localStorage.getItem("canales_employee") || "";
  const [name, setName] = useState(initialName);
  const [inputName, setInputName] = useState(initialName);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/roles/${slug}`);
        setRole(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Adopt authenticated user name when it becomes available (post-OAuth)
  useEffect(() => {
    if (user?.name && !name) {
      setName(user.name);
      setInputName(user.name);
      localStorage.setItem("canales_employee", user.name);
    }
  }, [user, name]);

  const refetchProgress = async (n) => {
    if (!n) return;
    try {
      const { data } = await api.get(
        `/public/roles/${slug}/progress/${encodeURIComponent(n)}`
      );
      setProgress(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!name) return;
    refetchProgress(name);
  }, [name, slug]);

  // Refetch progress when the tab regains focus — so that after reading the
  // comic in another tab (or returning) the "Paso 1" flips to "Leído" and the
  // exams automatically unlock.
  useEffect(() => {
    const onFocus = () => name && refetchProgress(name);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [name, slug]);

  const submitName = (e) => {
    e.preventDefault();
    const clean = inputName.trim();
    if (!clean) return;
    localStorage.setItem("canales_employee", clean);
    setName(clean);
  };

  const requiredExams = progress?.required || role?.required_exams || [];
  const bonusExams = progress?.bonus || role?.bonus_exams || [];
  const comicSlug = progress?.comic_slug ?? role?.comic_slug ?? "";
  const comicRead = !!progress?.comic_read;
  const comicMeta = useMemo(() => (comicSlug ? findComic(comicSlug) : null), [comicSlug]);
  // Exams are locked until the vendor reads the linked comic
  const examsLocked = !!comicSlug && !comicRead;

  const requiredProgress = useMemo(() => {
    if (!requiredExams.length) return 0;
    if (!progress) return 0;
    return Math.round((progress.required_passed / progress.required_total) * 100);
  }, [progress, requiredExams.length]);

  if (loading) return <div className="min-h-screen bg-halftone flex items-center justify-center font-display">Loading…</div>;

  if (notFound || !role) {
    return (
      <div className="min-h-screen bg-halftone">
        <Navbar />
        <div className="max-w-xl mx-auto brutal p-8 mt-10 bg-white text-center">
          <div className="font-display font-black text-2xl">404</div>
          <p className="font-body mt-2">
            {lang === "es" ? "Este rol no existe o fue eliminado." : "This role does not exist."}
          </p>
          <Link to="/onboarding" className="mt-3 inline-flex brutal-btn bg-canales-blue text-white px-4 py-2 text-sm">
            {lang === "es" ? "Ver todos los roles" : "See all roles"}
          </Link>
        </div>
      </div>
    );
  }

  const color = role.color || "#005BBB";

  return (
    <div className="min-h-screen bg-halftone" data-testid="onboarding-role-root">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <Link to="/onboarding" className="brutal-btn bg-white px-3 py-1 text-sm inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Todos los roles" : "All roles"}
        </Link>

        {/* Role header */}
        <div className="brutal p-6 text-white flex flex-wrap items-center justify-between gap-4" style={{ background: color }}>
          <div className="flex items-center gap-4">
            <CanalesLogo width={140} />
            <div>
              <div className="inline-flex items-center gap-2 bg-canales-yellow text-black px-3 py-1 rounded-full border-2 border-black font-display font-bold text-xs uppercase tracking-widest">
                <Briefcase className="w-4 h-4" /> {lang === "es" ? "Ruta por rol" : "Role path"}
              </div>
              <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl">
                {lang === "es" ? role.name_es : role.name_en}
              </h1>
              {(role.description_es || role.description_en) && (
                <p className="font-body opacity-90 mt-1 max-w-2xl">
                  {lang === "es" ? role.description_es : role.description_en}
                </p>
              )}
            </div>
          </div>
          <Mascot size={80} />
        </div>

        {/* Name gate */}
        <form onSubmit={submitName} className="mt-6 brutal p-4 bg-white flex flex-wrap items-center gap-3">
          <label className="font-display font-bold">
            {lang === "es" ? "Tu nombre:" : "Your name:"}
          </label>
          <input
            data-testid="role-name-input"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            placeholder={lang === "es" ? "Ej. María Pérez" : "e.g. Maria Perez"}
            className="brutal-sm px-3 py-2 font-body flex-1 min-w-[180px]"
          />
          <button
            type="submit"
            className="brutal-btn bg-canales-red text-white px-4 py-2 font-display font-bold"
          >
            {lang === "es" ? "Ver mi progreso" : "Show my progress"}
          </button>
          {progress && (
            <div className="flex items-center gap-2 ml-auto">
              <Trophy className="w-5 h-5" />
              <span className="font-display font-bold">
                {progress.required_passed}/{progress.required_total}
                {progress.bonus_total > 0 && ` · +${progress.bonus_passed}/${progress.bonus_total} bonus`}
              </span>
              {progress.role_expert && (
                <span className="brutal-btn bg-canales-yellow text-black px-3 py-1 text-sm font-display font-bold border-black">
                  ⭐ {lang === "es" ? "Rol dominado" : "Role mastered"}
                </span>
              )}
            </div>
          )}
        </form>

        {progress && requiredExams.length > 0 && (
          <div className="mt-4 brutal p-3 bg-canales-cream">
            <div className="flex items-center gap-2 text-sm font-display font-bold">
              {lang === "es" ? "Progreso obligatorio" : "Required progress"} · {requiredProgress}%
            </div>
            <div className="mt-1 h-3 bg-white border-2 border-black rounded-full overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${requiredProgress}%`, background: color }} />
            </div>
          </div>
        )}

        {/* Paso 1 · Cómic obligatorio */}
        {comicSlug && (
          <div
            className={`mt-6 brutal p-5 ${comicRead ? "bg-green-50" : "bg-white"}`}
            data-testid="role-comic-step"
            style={comicRead ? undefined : { borderColor: color }}
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div
                className="shrink-0 w-14 h-14 flex items-center justify-center border-4 border-black rounded-full font-display font-black text-2xl"
                style={{ background: comicRead ? "#16a34a" : color, color: "white" }}
              >
                1
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="brutal-sm bg-black text-canales-yellow px-2 py-0.5 text-[10px] font-display font-black tracking-widest uppercase">
                    {lang === "es" ? "Paso 1" : "Step 1"}
                  </span>
                  <span className="font-display font-black text-xl sm:text-2xl leading-tight">
                    {lang === "es" ? "Lee el cómic" : "Read the comic"}
                  </span>
                  {comicRead && (
                    <span className="inline-flex items-center gap-1 brutal-sm bg-green-600 text-white px-2 py-0.5 text-xs font-display font-black">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {lang === "es" ? "Leído" : "Read"}
                    </span>
                  )}
                </div>
                {comicMeta && (
                  <div className="mt-1 font-display font-bold text-base">
                    &ldquo;{comicMeta.title?.[lang]}&rdquo;
                  </div>
                )}
                <p className="font-body text-sm text-neutral-700 mt-1">
                  {comicRead
                    ? lang === "es"
                      ? "¡Cómic completado! Ya puedes tomar el examen."
                      : "Comic completed! You can now take the exam."
                    : lang === "es"
                    ? "Debes leer el cómic completo antes de contestar el examen obligatorio."
                    : "You must finish the comic before taking the required exam."}
                </p>
              </div>
              <Link
                to={`/comics/${comicSlug}`}
                data-testid="role-comic-open"
                className="brutal-btn text-white px-5 py-2 flex items-center gap-2 font-display font-black shrink-0"
                style={{ background: comicRead ? "#0d9488" : color }}
              >
                <BookOpen className="w-5 h-5" />
                {comicRead
                  ? lang === "es" ? "Releer cómic" : "Re-read comic"
                  : lang === "es" ? "Abrir cómic" : "Open comic"}
              </Link>
            </div>
          </div>
        )}

        {/* Required exams (Paso 2) */}
        <div className="mt-6 brutal p-5 bg-white" data-testid="role-required-section">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {comicSlug && (
              <div
                className="w-10 h-10 flex items-center justify-center border-4 border-black rounded-full font-display font-black text-lg"
                style={{ background: examsLocked ? "#9ca3af" : color, color: "white" }}
              >
                2
              </div>
            )}
            <h2 className="font-display font-black text-2xl flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6" />
              {lang === "es" ? "Exámenes obligatorios" : "Required exams"}
            </h2>
            <span className="text-sm font-body text-neutral-600">
              ({requiredExams.length})
            </span>
            {examsLocked && (
              <span className="brutal-sm bg-neutral-800 text-white px-2 py-0.5 text-[10px] font-display font-black tracking-widest uppercase inline-flex items-center gap-1">
                <Lock className="w-3 h-3" /> {lang === "es" ? "Bloqueado" : "Locked"}
              </span>
            )}
          </div>
          {examsLocked && (
            <div className="mb-3 brutal-sm bg-canales-yellow px-3 py-2 font-body text-sm">
              {lang === "es"
                ? "🔒 Primero termina el cómic (Paso 1) para desbloquear los exámenes."
                : "🔒 Finish the comic (Step 1) first to unlock the exams."}
            </div>
          )}
          {requiredExams.length === 0 ? (
            <div className="font-body text-sm text-neutral-600">
              {lang === "es" ? "No hay exámenes obligatorios en este rol." : "No required exams in this role."}
            </div>
          ) : (
            <ul className="space-y-2">
              {requiredExams.map((e) => (
                <ExamRow key={e.exam_id} exam={e} lang={lang} bonus={false} locked={examsLocked} />
              ))}
            </ul>
          )}
        </div>

        {/* Bonus exams */}
        {bonusExams.length > 0 && (
          <div className="mt-6 brutal p-5 bg-white" data-testid="role-bonus-section">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 fill-canales-yellow text-black" />
              <h2 className="font-display font-black text-2xl">
                {lang === "es" ? "Exámenes bonus" : "Bonus exams"}
              </h2>
              <span className="text-sm font-body text-neutral-600">
                ({bonusExams.length})
              </span>
            </div>
            <ul className="space-y-2">
              {bonusExams.map((e) => (
                <ExamRow key={e.exam_id} exam={e} lang={lang} bonus />
              ))}
            </ul>
          </div>
        )}

        {/* Bottom banner */}
        <div className="mt-8 brutal p-6 bg-canales-yellow flex items-center gap-4">
          <Carlos size={90} waving />
          <div className="flex-1">
            <div className="font-display font-black text-2xl">
              {progress?.role_expert
                ? (lang === "es" ? `¡Dominaste "${role.name_es}"!` : `You mastered "${role.name_en}"!`)
                : (lang === "es" ? "Termina todos los exámenes obligatorios" : "Finish all required exams")}
            </div>
            <p className="font-body">
              {progress?.role_expert
                ? (lang === "es"
                    ? "Pide a tu admin que emita tu certificado dorado 'Experto Canales'."
                    : "Ask your admin to issue your 'Canales Expert' golden certificate.")
                : (lang === "es"
                    ? `Con 90%+ en cada obligatorio te ganas el rango "Experto ${role.name_es}".`
                    : `With 90%+ on every required exam you unlock the "${role.name_en} Expert" rank.`)}
            </p>
          </div>
          {progress && (
            <div className="brutal-sm bg-white px-4 py-2 font-display font-bold text-sm">
              {progress.required_gold}/{progress.required_total} · 90%+
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
